import { isNullOrUndefinedOrEmpty, separateUrlProtocol } from 'utils'
import { AssetCategories, CreativeTypes } from 'features/Codes'
import { cloneDeep } from 'lodash'
import { tenthAx } from '../../api/axios/tenth'
import { getDominantColors } from '@kakao/color-extract'
import { initialThumbnailCustomState } from './add/asset/thumbnailCustomSlice'
import { initialThumbnailRecommendState } from './add/asset/thumbnailRecommendSlice'

export const getLandingUrlDto = ({ individual, pcUrl, mobileUrl }) => ({
  individualSettingYn: individual ? 'Y' : 'N',
  pcLandingUrl: individual
    ? pcUrl !== ''
      ? {
          protocol: separateUrlProtocol(pcUrl)[0].toUpperCase(),
          url: separateUrlProtocol(pcUrl)[1],
        }
      : null
    : {
        protocol: separateUrlProtocol(mobileUrl)[0].toUpperCase(),
        url: separateUrlProtocol(mobileUrl)[1],
      },
  mobileLandingUrl:
    individual && mobileUrl !== ''
      ? {
          protocol: separateUrlProtocol(mobileUrl)[0].toUpperCase(),
          url: separateUrlProtocol(mobileUrl)[1],
        }
      : null,
})

export const getLandingUrlState = ({
  individualSettingYn,
  mobileLandingUrl,
  pcLandingUrl,
}) => ({
  individual: individualSettingYn === 'Y',
  mobileUrl:
    individualSettingYn === 'N'
      ? pcLandingUrl
        ? `${pcLandingUrl.protocol.toLowerCase()}://${pcLandingUrl.url}`
        : ''
      : mobileLandingUrl
      ? `${mobileLandingUrl.protocol.toLowerCase()}://${mobileLandingUrl.url}`
      : '',
  pcUrl: pcLandingUrl
    ? `${pcLandingUrl.protocol.toLowerCase()}://${pcLandingUrl.url}`
    : '',
})

export const distributeAssetsByCategory = assets => ({
  image: assets.filter(asset => asset.category === AssetCategories.IMAGE.name),
  text: assets.filter(asset => asset.category === AssetCategories.TEXT.name),
  button: assets.filter(
    asset => asset.category === AssetCategories.BUTTON.name
  ),
  link: assets.filter(asset => asset.category === AssetCategories.LINK.name),
})

export const joinAssetNames = assets =>
  assets.length === 0 ? '-' : assets.map(e => e.description).join(', ')

export const getProgressPercent = progressEvent =>
  ((progressEvent.loaded * 100) / progressEvent.total).toFixed(0)

export const formSatisfied = (index, formArray, validateFunc) =>
  formArray.slice(0, index).every(element => validateFunc(element))

export const setLandingUrlFromChannel = (
  siteUrl,
  setMobileUrl,
  setPcUrl,
  setIndividual
) => {
  if (siteUrl.individualSettingYn === 'N') {
    setMobileUrl(
      siteUrl.pcUrl
        ? `${siteUrl.pcUrl.protocol.toLowerCase()}://${siteUrl.pcUrl.url}`
        : ''
    )
    setPcUrl(
      siteUrl.pcUrl
        ? `${siteUrl.pcUrl.protocol.toLowerCase()}://${siteUrl.pcUrl.url}`
        : ''
    )
  } else {
    setMobileUrl(
      siteUrl.mobileUrl
        ? `${siteUrl.mobileUrl.protocol.toLowerCase()}://${
            siteUrl.mobileUrl.url
          }`
        : ''
    )
    setPcUrl(
      siteUrl.pcUrl
        ? `${siteUrl.pcUrl.protocol.toLowerCase()}://${siteUrl.pcUrl.url}`
        : ''
    )
  }
  setIndividual(siteUrl.individualSettingYn === 'Y')
}

export const fillEmptyFieldValue = (obj, fieldName, value) => {
  const result = cloneDeep(obj)
  const helper = o => {
    if (o === null || o === undefined) {
      return
    }
    Object.keys(o).forEach(key => {
      if (key === fieldName && isNullOrUndefinedOrEmpty(o[key])) {
        o[key] = value
      } else if (typeof o[key] === 'object') {
        helper(o[key])
      }
    })
  }
  helper(result)
  return result
}

export const fillCreativeEmptyLandingUrl = (creative, channelUrl) => {
  if (!channelUrl) {
    return creative
  }
  const channelLandingUrl = {
    individualSettingYn: channelUrl.individualSettingYn,
    mobileLandingUrl: channelUrl.mobileUrl,
    pcLandingUrl: channelUrl.pcUrl,
  }
  return fillEmptyFieldValue(creative, 'landingUrl', channelLandingUrl)
}

export const sliceWithAltKeywordLength = (text = '', maxLength) => {
  const re = /(<키워드:.*?>)/g
  const splits = text.split(re)
  if (splits.length > 1) {
    let result = ''
    let count = 0
    splits.forEach(item => {
      const length = re.test(item) ? item.length - 6 : item.length
      if (count + length <= maxLength) {
        result = result + item
        count = count + length
      } else if (maxLength > count) {
        const sliced = item.slice(0, maxLength - count)
        result = result + sliced
        count = count + sliced.length
      }
    })
    return result
  } else {
    return text.slice(0, maxLength)
  }
}
const keywordTagRegex =
  /<키워드:([ㄱ-ㅎㅏ-ㅣ가-힣0-9a-zA-Z&\-+#./%@$'"=~:?*]+)>/g
export const stripKeywordTag = text =>
  text?.replace(keywordTagRegex, '$1') || ''
export const checkLoanInsurance = channel =>
  channel
    ? ['금융_대출', '금융_보험'].includes(
        channel.bizChannelCategory.categoryName
      )
    : false

export const checkMedical = channel =>
  channel
    ? [
        117, 118, 119, 121, 122, 123, 124, 125, 126, 127, 128, 129, 130, 131,
        132, 133, 134, 135, 136,
      ].includes(Number(channel.bizChannelCategory.id))
    : false

export const checkAdult = channel =>
  channel
    ? [450, 451, 452, 454, 453, 455].includes(
        Number(channel.bizChannelCategory.id)
      )
    : false

export const checkLegalNotice = channel => {
  if (channel) {
    const bizChannelCategoryId = Number(channel.bizChannelCategory.id)
    return (
      (bizChannelCategoryId >= 116 && bizChannelCategoryId <= 136) ||
      (bizChannelCategoryId >= 146 && bizChannelCategoryId <= 149) ||
      (bizChannelCategoryId >= 211 && bizChannelCategoryId <= 218) ||
      (bizChannelCategoryId >= 473 && bizChannelCategoryId <= 488) ||
      (bizChannelCategoryId >= 502 && bizChannelCategoryId <= 517) ||
      bizChannelCategoryId === 387 ||
      bizChannelCategoryId === 378 ||
      bizChannelCategoryId === 469 ||
      bizChannelCategoryId === 495
    )
  } else {
    return false
  }
}

export const getImageColors = async url => {
  const { data } = await tenthAx.get(url.replace('://t1', '://twg'), {
    responseType: 'blob',
  })
  const image = await createImageBitmap(data)
  const { width, height } = image
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.drawImage(image, 0, 0)
  const imageData = ctx.getImageData(0, 0, width, height)
  const dominantColors = getDominantColors(imageData, {
    colorFilter: {
      minimumSaturation: 0,
      maximumSaturation: 59,
      minimumValue: 31,
      maximumValue: 90,
    },
    maximumOutputSize: 7,
    fill: true,
    adjust: true,
  })
  return dominantColors.map(color => color.toString())
}

export const convertTalkChannelAssets = (assets = []) => {
  if (assets.length === 0) {
    return {
      thumbnailRecommend: initialThumbnailRecommendState,
      thumbnailCustom: initialThumbnailCustomState,
    }
  }
  return assets.reduce(
    (accumulator, { type: { name }, data }) => {
      const templateType =
        name === 'ACTION_INDUCTION' ||
        name === 'LANDING_URL' ||
        name === 'THUMBNAIL_CUSTOM' ||
        name === 'FONT_COLOR_LEGAL'
          ? 'THUMBNAIL_CUSTOM'
          : data.templateType || 'THUMBNAIL_RECOMMEND'
      const target =
        templateType === 'THUMBNAIL_CUSTOM'
          ? accumulator.thumbnailCustom
          : accumulator.thumbnailRecommend
      switch (name) {
        case 'THUMBNAIL_CUSTOM':
        case 'THUMBNAIL_RECOMMEND':
          target.thumbnail = {
            id: data.image.id,
            url: data.image.url,
            name: data.image.name,
            size: data.image.size,
            width: data.image.w,
            height: data.image.h,
          }
          break
        case 'BACKGROUND_COLOR':
          target.backgroundColor = data.color
          break
        case 'PROPOSAL_PHRASE_TOP':
          target.proposalPhraseTop = data.text
          break
        case 'PROPOSAL_PHRASE_BOTTOM':
          target.proposalPhraseBottom = data.text
          break
        case 'ACTION_INDUCTION':
          target.actionInduction = data.text
          break
        case 'FONT_COLOR':
          target.fontColor = data.color
          break
        case 'FONT_COLOR_LEGAL':
          target.fontColorLegal = data.color
          break
        case 'LANDING_URL':
          target.landingUrl = data.url
          break
        default:
      }
      return accumulator
    },
    {
      thumbnailRecommend: {
        proposalPhraseBottom: '',
      },
      thumbnailCustom: {},
    }
  )
}

const basicAssetOrder = [
  'THUMBNAIL',
  'MULTI_THUMBNAIL',
  'ADDITIONAL_TITLE',
  'ADDITIONAL_LINK',
  'SUBJECT',
  'PRICE_TABLE',
  'CALCULATE',
  'PHONE_NUMBER',
  'TALK_CHANNEL',
]
const sortAssets = assets =>
  assets.sort((a, b) => {
    const typeA = a?.type?.name
    const typeB = b?.type?.name
    const indexA = basicAssetOrder.indexOf(typeA)
    const indexB = basicAssetOrder.indexOf(typeB)
    return indexA - indexB
  })
export const sortCreativeResponse = response => {
  if (
    response?.data?.creative?.creativeType === CreativeTypes.BASIC &&
    Array.isArray(response?.data?.creative?.assets)
  ) {
    response.data.creative.assets = sortAssets(response.data.creative.assets)
  }
  return response
}
